// @lb-classroom/ui - Shared UI Component Library
// This is the public API for the UI library

// Utilities
export { cn } from './lib/utils';

// Button
export { Button, buttonVariants } from './components/button';
export type { ButtonProps } from './components/button';

// Card
export {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  cardVariants,
} from './components/card';
export type { CardProps } from './components/card';

// Alert
export { Alert, AlertTitle, AlertDescription } from './components/alert';

// Separator
export { Separator } from './components/separator';

// Dialog
export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from './components/dialog';

// Badge
export { Badge, badgeVariants } from './components/badge';
export type { BadgeProps } from './components/badge';

// Avatar
export { Avatar, AvatarImage, AvatarFallback } from './components/avatar';

// Checkbox
export { Checkbox } from './components/checkbox';

// Tooltip
export {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from './components/tooltip';

// DropdownMenu
export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuGroup,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuRadioGroup,
} from './components/dropdown-menu';

// Table
export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
} from './components/table';

// Typography
export {
  H1,
  H2,
  H3,
  H4,
  H5,
  H6,
  P,
  Lead,
  Large,
  Small,
  Muted,
  Blockquote,
  InlineCode,
  List,
} from './components/typography';

// Label
export { Label } from './components/label';

// Select
export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
} from './components/select';

// Form
export {
  useFormField,
  Form,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
  FormField,
} from './components/form';

// Sonner (Toast notifications)
export { Toaster, toast } from './components/sonner';

// Slider
export { Slider } from './components/slider';
export type { SliderProps } from './components/slider';

// Popover
export {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverAnchor,
} from './components/popover';

// Calendar
export { Calendar } from './components/calendar';
export type { CalendarProps } from './components/calendar';

// DatePicker
export { DatePicker } from './components/date-picker';
export type { DatePickerProps } from './components/date-picker';

// DateRangePicker
export { DateRangePicker } from './components/date-range-picker';
export type { DateRange, DateRangePickerProps } from './components/date-range-picker';

// Pagination
export { Pagination } from './components/pagination';
export type { PaginationProps } from './components/pagination';

// Input
export { Input, inputVariants } from './components/input';
export type { InputProps } from './components/input';

// Textarea
export { Textarea } from './components/textarea';
export type { TextareaProps } from './components/textarea';

// RadioGroup
export { RadioGroup, RadioGroupItem } from './components/radio-group';

// Media Types
export type { MediaDevice } from './types/media.types';

// Device Controls
export { DeviceControlButton } from './components/device-control-button';
export type { DeviceControlButtonProps } from './components/device-control-button';

export { DeviceSelector } from './components/device-selector';
export type { DeviceSelectorProps } from './components/device-selector';

// Video Preview
export { VideoPreview } from './components/video-preview';
export type { VideoPreviewProps } from './components/video-preview';

// Call Controls
export { CallControlButton } from './components/call-control-button';
export type {
  CallControlButtonProps,
  CallControlIcon,
} from './components/call-control-button';

// Session Tab Bar
export {
  SessionTabBar,
  MobileSessionTabBar,
} from './components/session-tab-bar';
export type {
  SessionTab,
  SessionTabBarProps,
  MobileSessionTabBarProps,
} from './components/session-tab-bar';

// Tabs
export { Tabs, TabsList, TabsTrigger, TabsContent } from './components/tabs';

// Accordion
export {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from './components/accordion';
